import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabaseClient';

import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData(prev => ({
      ...prev,
      rememberMe: e?.target?.checked
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    const newErrors = {};
    
    if (!formData?.email?.trim()) {
      newErrors.email = '이메일을 입력해주세요';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다';
    }
    
    if (!formData?.password?.trim()) {
      newErrors.password = '비밀번호를 입력해주세요';
    } else if (formData?.password?.length < 6) {
      newErrors.password = '비밀번호는 최소 6자 이상이어야 합니다';
    }
    
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsLoading(true);

    const emailTrimmed = formData?.email?.trim();
    const passwordTrimmed = formData?.password?.trim();

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseRef = typeof supabaseUrl === 'string' ? supabaseUrl.split('.')[0]?.replace('https://', '') : undefined;

    console.groupCollapsed('[AdminLogin] signInWithPassword');
    console.log('supabaseUrl:', supabaseUrl);
    console.log('supabaseRef:', supabaseRef);
    console.log('email:', emailTrimmed);
    console.log('passwordLength:', passwordTrimmed?.length || 0);
    console.log('passwordHasLeadingOrTrailingSpaces:', formData?.password !== passwordTrimmed);
    console.log('time:', new Date().toISOString());
    console.groupEnd();

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: emailTrimmed,
        password: passwordTrimmed
      });

      if (error) {
        console.group('[AdminLogin] signInWithPassword error');
        console.error('message:', error?.message);
        console.error('status:', error?.status);
        console.error('name:', error?.name);
        console.error('code:', error?.code);
        console.error('details:', error);
        console.groupEnd();
        setErrors({ general: '이메일 또는 비밀번호가 올바르지 않습니다' });
        setIsLoading(false);
        return;
      }

      console.groupCollapsed('[AdminLogin] signInWithPassword success');
      console.log('userId:', data?.user?.id);
      console.log('sessionExpiresAt:', data?.session?.expires_at);
      console.groupEnd();

      const userId = data?.user?.id;
      if (!userId) {
        setErrors({ general: '로그인 정보를 확인할 수 없습니다' });
        setIsLoading(false);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) {
        console.group('[AdminLogin] profiles select error');
        console.error('message:', profileError?.message);
        console.error('details:', profileError);
        console.groupEnd();
        setErrors({ general: '권한 정보를 불러오지 못했습니다' });
        setIsLoading(false);
        return;
      }

      const role = profile?.role || 'clients';
      const next = role === 'admin' ? '/super-admin/admins' : '/session-list-dashboard';
      const from = location?.state?.from;
      navigate(from || next);
    } catch (err) {
      setErrors({ general: '로그인 중 오류가 발생했습니다' });
      setIsLoading(false);
    }
  };

  const handlePasswordReset = () => {
    alert('비밀번호 재설정 기능은 관리자에게 문의해주세요');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-xl shadow-lg border border-border p-6 md:p-8 lg:p-10">
          <div className="flex flex-col items-center mb-6 md:mb-8">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
              <Icon name="Dumbbell" size={32} color="var(--color-primary)" />
            </div>
            
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground text-center mb-2">
              PickleLink 관리자
            </h1>
            
            <p className="text-sm md:text-base caption text-muted-foreground text-center">
              시설 관리 시스템에 로그인하세요
            </p>
          </div>

          {errors?.general && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-md flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-error mb-1">
                  로그인 실패
                </p>
                <p className="text-sm caption text-error/80">
                  {errors?.general}
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
            <Input
              type="email"
              name="email"
              label="이메일"
              placeholder="admin@picklelink.com"
              value={formData?.email}
              onChange={handleInputChange}
              error={errors?.email}
              required
              disabled={isLoading}
            />

            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                label="비밀번호"
                placeholder="비밀번호를 입력하세요"
                value={formData?.password}
                onChange={handleInputChange}
                error={errors?.password}
                required
                disabled={isLoading}
              />
              
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[38px] p-2 text-muted-foreground hover:text-foreground transition-athletic"
                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <Checkbox
                label="로그인 상태 유지"
                checked={formData?.rememberMe}
                onChange={handleCheckboxChange}
                disabled={isLoading}
              />
              
              <button
                type="button"
                onClick={handlePasswordReset}
                className="text-sm font-medium text-primary hover:text-primary/80 transition-athletic"
                disabled={isLoading}
              >
                비밀번호 찾기
              </button>
            </div>

            <Button
              type="submit"
              variant="default"
              size="lg"
              loading={isLoading}
              iconName="LogIn"
              iconPosition="right"
              fullWidth
            >
              로그인
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm caption text-muted-foreground">
              계정이 없으신가요?{' '}
              <button
                type="button"
                onClick={() => alert('신규 시설 등록은 고객센터로 문의해주세요')}
                className="font-medium text-primary hover:text-primary/80 transition-athletic"
              >
                시설 등록 문의
              </button>
            </p>
          </div>
        </div>

        <div className="mt-6 md:mt-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-success/10 border border-success/20 rounded-md">
              <Icon name="Shield" size={16} color="var(--color-success)" />
              <span className="text-sm font-medium text-success">보안 연결</span>
            </div>
            
            <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-md">
              <Icon name="MessageCircle" size={16} color="var(--color-primary)" />
              <span className="text-sm font-medium text-primary">카카오톡 연동</span>
            </div>
          </div>
          
          <p className="text-xs caption text-muted-foreground">
            © 2026 PickleLink. 모든 권리 보유.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;